import React, { useState, useEffect } from 'react';
import { 
  EnvelopeIcon, 
  ChartBarIcon, 
  CalendarDaysIcon, 
  CurrencyDollarIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  Squares2X2Icon,
  ChevronDownIcon,
  ChevronRightIcon 
} from '@heroicons/react/24/outline';
import { calculateCost } from '../utils/costCalculator';
import { buildApiUrl } from '../config/api';
import Cookies from 'js-cookie';

const StatsManager = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accountStats, setAccountStats] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [summary, setSummary] = useState([]);
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showMonthlySummary, setShowMonthlySummary] = useState(false);
  const [expandedAccounts, setExpandedAccounts] = useState(new Set());
  const [accountHistory, setAccountHistory] = useState({});
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  // Cargar cuentas al montar el componente
  useEffect(() => {
    loadAccounts();
    loadSummary();
  }, []);

  // Cargar estadísticas cuando se selecciona una cuenta
  useEffect(() => {
    if (selectedAccount) {
      loadAccountStats();
      loadMonthlyStats();
    }
  }, [selectedAccount, dateRange]);

  const loadAccounts = async () => {
    try {
      const response = await fetch(buildApiUrl('accounts_manager.php'), {
        headers: {
          'Authorization': `Bearer ${Cookies.get('auth_token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setAccounts(data.data);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadAccountStats = async () => {
    if (!selectedAccount) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        buildApiUrl(`stats_manager.php?action=account_stats&account_id=${selectedAccount}&start_date=${dateRange.start}&end_date=${dateRange.end}`),
        {
          headers: {
            'Authorization': `Bearer ${Cookies.get('auth_token')}`
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setAccountStats(data.data);
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Error al cargar estadísticas de la cuenta');
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyStats = async () => {
    if (!selectedAccount) return;
    
    try {
      const response = await fetch(
        buildApiUrl(`stats_manager.php?action=monthly_stats&account_id=${selectedAccount}&months=6`),
        {
          headers: {
            'Authorization': `Bearer ${Cookies.get('auth_token')}`
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setMonthlyStats(data.data);
      }
    } catch (error) {
      console.error('Error loading monthly stats:', error);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await fetch(buildApiUrl('stats_manager.php?action=summary'), {
        headers: {
          'Authorization': `Bearer ${Cookies.get('auth_token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSummary(data.data);
      }
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const loadAccountHistory = async (accountId) => {
    try {
      const response = await fetch(
        buildApiUrl(`stats_manager.php?action=account_history&account_id=${accountId}&months=12`),
        {
          headers: {
            'Authorization': `Bearer ${Cookies.get('auth_token')}`
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setAccountHistory(prev => ({
          ...prev,
          [accountId]: data.data
        }));
      }
    } catch (error) {
      console.error('Error loading account history:', error);
    }
  };

  const toggleAccountExpansion = (accountId) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId);
    } else {
      newExpanded.add(accountId);
      // Cargar historial si no existe
      if (!accountHistory[accountId]) {
        loadAccountHistory(accountId);
      }
    }
    setExpandedAccounts(newExpanded);
  };

  const updateAllStats = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(buildApiUrl('stats_manager.php?action=update_all'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${Cookies.get('auth_token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Estadísticas actualizadas correctamente');
        loadSummary();
        if (selectedAccount) {
          loadAccountStats();
          loadMonthlyStats();
        }
      } else {
        setError(data.message || 'Error al actualizar estadísticas');
      }
    } catch (error) {
      setError('Error de conexión: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    // Convertir a número si es string y validar
    const number = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(number) || number === null || number === undefined) {
      return '0';
    }
    return new Intl.NumberFormat('en-US').format(Math.floor(number));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Función para agrupar datos por mes
  const groupDataByMonth = (data) => {
    const grouped = {};
    data.forEach(account => {
      const lastUpdate = account.last_update;
      if (lastUpdate && lastUpdate !== 'Sin datos') {
        const monthKey = lastUpdate.substring(0, 7); // YYYY-MM
        if (!grouped[monthKey]) {
          grouped[monthKey] = {
            month: monthKey,
            accounts: [],
            totalUsed: 0,
            totalCost: 0,
            activeAccounts: 0
          };
        }
        grouped[monthKey].accounts.push(account);
        grouped[monthKey].totalUsed += account.monthly_used || 0;
        grouped[monthKey].totalCost += calculateCost(account.monthly_used || 0);
        if (account.is_active) {
          grouped[monthKey].activeAccounts += 1;
        }
      }
    });
    return Object.values(grouped).sort((a, b) => b.month.localeCompare(a.month));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Estadísticas y Reportes</h1>
          <p className="text-gray-600 mt-2">
            Monitorea el consumo de emails de tus cuentas SendGrid
          </p>
        </div>
        <button 
          onClick={updateAllStats} 
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Actualizar Todo</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="space-y-4">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('summary')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'summary'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Resumen General
            </button>
            <button
              onClick={() => setActiveTab('detailed')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'detailed'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Análisis Detallado
            </button>
            <button
              onClick={() => setActiveTab('historical')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'historical'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Historial Completo
            </button>
          </nav>
        </div>

        {activeTab === 'summary' && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {summary.map((account) => (
              <div key={account.id} className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {account.name}
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      account.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {account.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center space-x-2">
                      <EnvelopeIcon className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-900">
                        {formatNumber(account.latest_used)} / {formatNumber(account.latest_total)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ChartBarIcon className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-900">
                        {formatNumber(account.monthly_used)} este mes
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CurrencyDollarIcon className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-900">
                        {formatCurrency(calculateCost(account.monthly_used || 0))} este mes
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CalendarDaysIcon className="h-4 w-4 text-gray-500" />
                      <span className="text-xs text-gray-500">
                        {account.last_update}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'detailed' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
                Análisis Detallado por Cuenta
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Selecciona una cuenta para ver estadísticas detalladas
              </p>
              <div className="flex space-x-4 mb-6">
                <select 
                  value={selectedAccount} 
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  className="block w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seleccionar cuenta</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id.toString()}>
                      {account.name}
                    </option>
                  ))}
                </select>
                
                <div className="flex space-x-2">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {selectedAccount && (
                <div className="space-y-6">
                  {/* Resumen de estadísticas mensuales */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Resumen Mensual</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {monthlyStats.slice(0, 3).map((stat, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg shadow-sm">
                          <div className="text-sm text-gray-600">{stat.month}</div>
                          <div className="text-2xl font-bold text-gray-900">{formatNumber(stat.total_used)}</div>
                          <div className="text-sm text-gray-500">emails enviados</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tabla de datos detallados */}
                  <div className="bg-white shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">Datos Detallados</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enviados</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Restantes</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Costo</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {accountStats.map((stat, index) => (
                              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stat.date}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatNumber(stat.used_emails)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatNumber(stat.remaining_emails)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatNumber(stat.total_emails)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(stat.cost)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'historical' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
                Historial Completo de Estadísticas
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Visualiza el historial completo de todas las cuentas con datos almacenados en la base de datos
              </p>
              


              {/* Resumen general histórico */}
              <div className="mb-8">
                <h4 className="text-md font-medium text-gray-900 mb-4">Resumen General Histórico</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-sm text-blue-600 font-medium">Total de Cuentas</div>
                    <div className="text-2xl font-bold text-blue-900">{summary.length}</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-sm text-green-600 font-medium">Cuentas Activas</div>
                    <div className="text-2xl font-bold text-green-900">
                      {summary.filter(acc => acc.is_active).length}
                    </div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <div className="text-sm text-orange-600 font-medium">Última Actualización</div>
                    <div className="text-sm font-bold text-orange-900">
                      {summary.length > 0 ? summary[0].last_update : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Toggle para vista mensual */}
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Squares2X2Icon className="h-5 w-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Vista de datos:</span>
                  <button
                    onClick={() => setShowMonthlySummary(!showMonthlySummary)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      showMonthlySummary ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showMonthlySummary ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-sm text-gray-600">
                    {showMonthlySummary ? 'Resúmenes Mensuales' : 'Vista Detallada'}
                  </span>
                </div>
              </div>

              {/* Tabla histórica completa */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h4 className="text-md font-medium text-gray-900 mb-4">
                    {showMonthlySummary ? 'Resúmenes por Mes' : 'Datos Históricos por Cuenta'}
                  </h4>
                  <div className="overflow-x-auto">
                    {!showMonthlySummary ? (
                      // Vista detallada por cuenta
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cuenta</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Último Usado</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Disponible</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Costo (Mes)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Última Actualización</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Historial</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {summary.map((account, index) => (
                            <React.Fragment key={account.id}>
                              <tr 
                                className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 cursor-pointer transition-colors duration-150`}
                                onClick={() => toggleAccountExpansion(account.id)}
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{account.name}</div>
                                  <div className="text-sm text-gray-500">ID: {account.id}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    account.is_active 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {account.is_active ? 'Activa' : 'Inactiva'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatNumber(account.latest_used)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatNumber(account.latest_total)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatCurrency(calculateCost(account.monthly_used || 0))}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {account.last_update}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <div className="inline-flex items-center p-1 text-gray-400">
                                    {expandedAccounts.has(account.id) ? (
                                      <ChevronDownIcon className="h-5 w-5" />
                                    ) : (
                                      <ChevronRightIcon className="h-5 w-5" />
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedAccounts.has(account.id) && (
                                <tr>
                                  <td colSpan="7" className="px-6 py-4 bg-blue-50 border-l-4 border-blue-400 shadow-inner">
                                    <div className="max-w-full bg-white rounded-lg p-4 shadow-sm border border-blue-200">
                                      <h5 className="text-sm font-medium text-gray-900 mb-3">
                                        Historial Mensual - {account.name}
                                      </h5>
                                      {accountHistory[account.id] ? (
                                        <div className="overflow-x-auto">
                                          <table className="min-w-full divide-y divide-gray-200 rounded-lg overflow-hidden">
                                            <thead className="bg-blue-100">
                                              <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mes</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Emails Enviados</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Costo</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                                              </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                              {accountHistory[account.id].map((historyItem, historyIndex) => (
                                                <tr key={historyIndex} className={historyIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                                    {new Date(historyItem.date + '-01').toLocaleDateString('es-ES', { 
                                                      year: 'numeric', 
                                                      month: 'long' 
                                                    })}
                                                  </td>
                                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                                    {formatNumber(historyItem.used_emails)}
                                                  </td>
                                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                                    {formatCurrency(calculateCost(historyItem.used_emails || 0))}
                                                  </td>
                                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                                    {historyItem.date}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : (
                                        <div className="text-sm text-gray-500">Cargando historial...</div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      // Vista de resúmenes mensuales
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mes</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cuentas Activas</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Cuentas</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Costo Total</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Promedio por Cuenta</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {groupDataByMonth(summary).map((monthData, index) => (
                            <tr key={monthData.month} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {new Date(monthData.month + '-01').toLocaleDateString('es-ES', { 
                                    year: 'numeric', 
                                    month: 'long' 
                                  })}
                                </div>
                                <div className="text-sm text-gray-500">{monthData.month}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {monthData.activeAccounts}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {monthData.accounts.length}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(monthData.totalCost)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(monthData.accounts.length > 0 ? monthData.totalCost / monthData.accounts.length : 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>

              {/* Información sobre el sistema de historial */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Sistema de Historial Implementado
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        • Las estadísticas se almacenan automáticamente en la base de datos<br/>
                        • Puedes consultar datos históricos de meses anteriores<br/>
                        • El sistema actualiza los datos diariamente de forma automática<br/>
                        • Los datos se obtienen directamente de SendGrid y se guardan para consulta posterior
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsManager;